import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoneyFromCents } from '@theme/paint-calculator-core';
import { getBundledCartSectionIds } from '@theme/cart-section-ids';

/**
 * Voortgangsbalk naar een gratis cadeau op orderwaarde (MKT-1705).
 *
 * Zie de doc-blok in `snippets/cart-gift-bar.liquid`. Kort: mixed-offer.js publiceert na
 * elke doorrekening het plan van de engine (`mixed-offer:plan`, laatste stand op
 * `window.__MIXED_OFFER_LAST_PLAN__`); wij tonen de eerste drempel daaruit. De drawer
 * rendert zijn summary opnieuw bij elke mandwijziging, dus dit element wordt vaak
 * opnieuw aangemaakt — daarom lezen we bij het aankoppelen eerst de laatste stand.
 *
 * "Voeg toe" is de enige mutatie: één seed-regel met precies de properties die de engine
 * teruggeeft, zodat cart transformer en checkout-regel de regel herkennen. Mixed-offer.js
 * rekent daarna zelf na; wordt de regel geweigerd (drempel intussen niet meer gehaald,
 * of het cadeau ligt er al), dan tonen we de melding van Shopify onder de balk.
 *
 * @extends {Component}
 */

/**
 * @typedef {object} ThresholdProgress
 * @property {string} campaignId
 * @property {string|null} [campaignTitle]
 * @property {string} crossSellItemId
 * @property {string|number} variantId
 * @property {string} [title]
 * @property {string} [image]
 * @property {Record<string, string>} [properties]
 * @property {number} thresholdCents
 * @property {number} currentCents
 * @property {number} remainingCents
 * @property {boolean} qualified
 * @property {boolean} inCart
 */

const MEET_PUBLISH_RETRY_MS = 50;
const MEET_PUBLISH_DEADLINE_MS = 8000;

/**
 * Meet-event naar de Shopify Web Pixels; zelfde patroon als free-gifts.js.
 * @param {string} naam
 * @param {Record<string, unknown>} payload
 */
function publiceerMeetEvent(naam, payload) {
  const deadline = Date.now() + MEET_PUBLISH_DEADLINE_MS;
  const tick = () => {
    try {
      const publish = window.Shopify?.analytics?.publish;
      if (typeof publish === 'function') {
        publish(naam, payload);
        return;
      }
    } catch {
      return;
    }
    if (Date.now() > deadline) return;
    setTimeout(tick, MEET_PUBLISH_RETRY_MS);
  };
  tick();
}

/**
 * De drempel die de balk toont: de eerste die nog niet gehaald is, anders de eerste.
 * Meer dan één drempel-actie tegelijk verwachten we niet; dan wint de nog te halen.
 * @param {ThresholdProgress[]} thresholds
 * @returns {ThresholdProgress | null}
 */
function kiesDrempel(thresholds) {
  if (!Array.isArray(thresholds) || !thresholds.length) return null;
  return thresholds.find((t) => t && !t.qualified) ?? thresholds[0] ?? null;
}

/**
 * Korte naam voor in de zin: alles vóór ' - ', net als de kop van het cadeau-blok.
 * @param {string} naam
 */
function kortNaam(naam) {
  return String(naam || '').split(' - ')[0].trim();
}

export class CartGiftBar extends Component {
  /** @type {ThresholdProgress | null} */
  #drempel = null;

  #bezig = false;

  /** Laatst gemelde stand, zodat een her-render van de drawer niet opnieuw meet. */
  static #gemeld = '';

  /** @type {MutationObserver | undefined} */
  #morphObserver;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mixed-offer:plan', this.#onPlan);
    this.addEventListener('click', this.#onClick);
    // De drawer morpht zijn summary bij elke mandwijziging over de bestaande elementen
    // heen: dan komt de server-huls (hidden, lege tekst) terug op ditzelfde element,
    // zonder connectedCallback. Zien we `hidden` terugkomen terwijl we een drempel
    // kennen, dan tekenen we de laatste stand meteen opnieuw — het plan van de engine
    // volgt daarna vanzelf via `mixed-offer:plan`.
    this.#morphObserver = new MutationObserver(() => {
      if (this.hidden && this.#drempel) this.#toon(this.#drempel);
    });
    this.#morphObserver.observe(this, { attributes: true, attributeFilter: ['hidden'] });
    const laatste = /** @type {any} */ (window).__MIXED_OFFER_LAST_PLAN__;
    this.#toon(kiesDrempel(laatste?.thresholds));
  }

  disconnectedCallback() {
    document.removeEventListener('mixed-offer:plan', this.#onPlan);
    this.removeEventListener('click', this.#onClick);
    this.#morphObserver?.disconnect();
    this.#morphObserver = undefined;
    super.disconnectedCallback();
  }

  /** @param {Event} event */
  #onPlan = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    this.#toon(kiesDrempel(detail?.thresholds));
  };

  /** @param {ThresholdProgress | null} drempel */
  #toon(drempel) {
    this.#drempel = drempel;

    if (!drempel || !(drempel.thresholdCents > 0)) {
      this.hidden = true;
      return;
    }

    const gift = kortNaam(drempel.title || '') || 'cadeau';
    const stand = drempel.inCart ? 'in-cart' : drempel.qualified ? 'reached' : 'remaining';
    const sjabloon = this.dataset[stand === 'in-cart' ? 'textInCart' : stand === 'reached' ? 'textReached' : 'textRemaining'] || '';

    const tekst = this.querySelector('[data-gift-text]');
    if (tekst instanceof HTMLElement) {
      tekst.replaceChildren(
        ...this.#vulSjabloon(sjabloon, {
          amount: this.#formatMoney(drempel.remainingCents),
          gift,
        })
      );
    }

    const thumb = this.querySelector('[data-gift-thumb]');
    if (thumb instanceof HTMLElement) {
      if (drempel.image) {
        if (!thumb.classList.contains('cart-gift-bar__thumb--image')) {
          const img = document.createElement('img');
          img.src = drempel.image;
          img.alt = '';
          img.width = 34;
          img.height = 34;
          img.loading = 'lazy';
          thumb.replaceChildren(img);
          thumb.classList.add('cart-gift-bar__thumb--image');
        } else {
          const img = thumb.querySelector('img');
          if (img && img.src !== drempel.image) img.src = drempel.image;
        }
      }
    }

    const percent = Math.max(0, Math.min(100, Math.round((drempel.currentCents / drempel.thresholdCents) * 100)));
    const fill = this.querySelector('[data-gift-fill]');
    if (fill instanceof HTMLElement) fill.style.width = `${drempel.qualified ? 100 : percent}%`;

    const track = this.querySelector('[data-gift-track]');
    if (track instanceof HTMLElement) track.hidden = stand === 'in-cart';

    const knop = this.querySelector('[data-gift-add]');
    if (knop instanceof HTMLButtonElement) {
      knop.hidden = stand !== 'reached';
      knop.disabled = this.#bezig;
      knop.textContent = this.#bezig ? this.dataset.addBusyLabel || 'Toevoegen…' : this.dataset.addLabel || 'Voeg toe';
    }

    this.classList.toggle('cart-gift-bar--qualified', drempel.qualified);
    this.dataset.state = stand;
    this.#toonFout('');
    this.hidden = false;

    this.#meet(stand, drempel);
  }

  /**
   * "{amount}" en "{gift}" worden vet; de rest is tekst. Geen innerHTML met copy erin.
   * @param {string} sjabloon
   * @param {Record<string, string>} waarden
   * @returns {Node[]}
   */
  #vulSjabloon(sjabloon, waarden) {
    /** @type {Node[]} */
    const nodes = [];
    const delen = sjabloon.split(/(\{amount\}|\{gift\})/);
    for (const deel of delen) {
      if (!deel) continue;
      const sleutel = deel === '{amount}' ? 'amount' : deel === '{gift}' ? 'gift' : '';
      if (sleutel) {
        const strong = document.createElement('strong');
        strong.textContent = waarden[sleutel] ?? '';
        nodes.push(strong);
      } else {
        nodes.push(document.createTextNode(deel));
      }
    }
    return nodes;
  }

  /**
   * Eén meting per stand per mand-toestand, ook al rendert de drawer de balk vaker.
   * @param {string} stand
   * @param {ThresholdProgress} drempel
   */
  #meet(stand, drempel) {
    const sleutel = `${drempel.crossSellItemId}:${stand}:${drempel.currentCents}`;
    if (CartGiftBar.#gemeld === sleutel) return;
    CartGiftBar.#gemeld = sleutel;
    publiceerMeetEvent('hds:gift_bar', {
      stand,
      cadeau: drempel.title || '',
      drempel_cents: drempel.thresholdCents,
      huidig_cents: drempel.currentCents,
      resterend_cents: drempel.remainingCents,
    });
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-gift-add]')) return;
    event.preventDefault();
    void this.#voegToe();
  };

  async #voegToe() {
    const drempel = this.#drempel;
    if (!drempel || this.#bezig || !drempel.qualified || drempel.inCart) return;

    this.#bezig = true;
    this.#toon(drempel);

    const sectionIds = getBundledCartSectionIds();
    /** @type {Record<string, unknown>} */
    const body = {
      items: [
        {
          id: Number(drempel.variantId) || drempel.variantId,
          quantity: 1,
          properties: drempel.properties && Object.keys(drempel.properties).length
            ? drempel.properties
            : { _cross_sell: 'true', _is_free: 'true' },
        },
      ],
    };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    try {
      // `Theme` is een lexicale globale uit scripts.liquid (niet op window); zonder de
      // .js-route antwoordt Shopify met een redirect naar /cart in plaats van JSON.
      const cartAddUrl = (typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url) || '/cart/add.js';
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Ongeldige reactie van winkelmand');
      }
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Toevoegen aan winkelmand mislukt');
      }

      publiceerMeetEvent('hds:gift_add', {
        cadeaus: [drempel.title || ''],
        aantal: 1,
        hoofdproduct_id: '',
        bron: 'cart-gift-bar',
      });

      // Zelfde event als het PDP-vinkje: de drawer rendert opnieuw en mixed-offer.js rekent na.
      document.dispatchEvent(
        new CartAddEvent(data, 'cart-gift-bar', {
          source: 'cart-gift-bar',
          itemCount: data.item_count,
          sections: data.sections,
        })
      );
    } catch (error) {
      this.#bezig = false;
      this.#toon(drempel);
      this.#toonFout(error instanceof Error ? error.message : 'Toevoegen aan winkelmand mislukt');
      return;
    }

    this.#bezig = false;
  }

  /** @param {string} melding */
  #toonFout(melding) {
    const el = this.querySelector('[data-gift-error]');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = melding;
    el.hidden = !melding;
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    return formatMoneyFromCents(cents, this.dataset.moneyFormat || '{{amount}}').replace(',00', ',-');
  }
}

if (!customElements.get('cart-gift-bar')) {
  customElements.define('cart-gift-bar', CartGiftBar);
}
